import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps, IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';

import ErrorBoundary from '../../containers/ErrorBoundary/ErrorBoundary';
import ErrorCatch from '../../containers/ErrorBoundary/ErrorCatch';
import { localeData } from '../../locales/data';
import { StateType } from '../../reducers';
import { expressionFetchingSelector } from '../../reducers/ExpressionLanguageReducer/ExpressionLanguageSelector';
import { fastExpressionFetchingSelector } from '../../reducers/FastExpressionLanguageReducer/FastExpressionLanguageSelector';
import { languageFieldsFetchingSelector } from '../../reducers/LanguageFieldsReducer/LanguageFieldsSelector';
import { addModifiers, calculateEditorHeight } from '../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { AlphaDataTable } from '../AlphaDataTable/AlphaDataTable';

import './AlphaCode.scss';
import AlphaMonaco from './AlphaMonaco';
import CloneAlpha from '../Learn/LearnBlocks/CloneAlpha';
import { Button } from 'semantic-ui-react';

const MAX_VISIBLE_LINES_DEFAULT = 5;

interface AlphaCodeMessage {
  id: string;
  values?: Record<string, string>;
}

interface AlphaCodeProps {
  alpha: any;
  codeElem: string;
  disabled?: boolean;
  editorWillMount: () => void;
  handleCloneAlphaFromContent?: () => void;
  hasTitle?: boolean;
  id?: string;
  isMultiTab?: boolean;
  isPopupContent?: boolean;
  learnBlockId?: string;
  maxHeight?: boolean;
  message?: AlphaCodeMessage;
  modifiers?: string[];
  settingsOptions?: any;
  showClone?: boolean;
  title?: string;
}

interface AlphaCodeStateToProps {
  isLanguageTokensFetching: boolean;
}

type AlphaCodeGenericProps = AlphaCodeProps & AlphaCodeStateToProps & InjectedIntlProps;

const handleEditorDidMount = (editor: any, codeElem: string, maxVisibleLines?: number) => {
  const container: any = document.querySelector(`.${codeElem}`);

  const updateEditorLayout = () => {
    const width = container.offsetWidth;
    const numberOfLines = editor.getModel().getLineCount();
    const height = calculateEditorHeight(numberOfLines, { maxVisibleLines });
    editor.layout({ height, width });
  };

  updateEditorLayout();
  editor.onDidChangeModelContent((_: any) => updateEditorLayout());
};

const getSettingsTable = (intl: IntlShape, type: string) => [
  { title: formatMessageHelper(intl, 'alpha.code.region'), id: 'region' },
  { title: formatMessageHelper(intl, 'alpha.code.universe'), id: 'universe' },
  { title: formatMessageHelper(intl, 'alpha.code.language'), id: 'language' },
  { title: formatMessageHelper(intl, 'alpha.code.decay'), id: 'decay' },
  { title: formatMessageHelper(intl, 'alpha.code.delay'), id: 'delay' },
  { title: formatMessageHelper(intl, 'alpha.code.truncation'), id: 'truncation' },
  { title: formatMessageHelper(intl, 'alpha.code.neutralization'), id: 'neutralization' },
  { title: formatMessageHelper(intl, 'alpha.code.pasteurization'), id: 'pasteurization' },
  { title: formatMessageHelper(intl, 'alpha.code.lookback'), id: 'lookback' },
  { title: formatMessageHelper(intl, 'alpha.code.maxTrade'), id: 'maxTrade' },
  { title: formatMessageHelper(intl, 'alpha.code.maxPosition'), id: 'maxPosition' },
  ...(type === 'SUPER' ? [{ title: formatMessageHelper(intl, 'alpha.code.componentActivation'), id: 'componentActivation' }] : []),
];

export const getSettingLabel = (settingsOptions: any, id: string, value: any, instrumentType: string, region?: string) => {
  const regionPath = [id, 'choices', 'instrumentType', instrumentType, 'region', region];
  const instrumentTypePath = [id, 'choices', 'instrumentType', instrumentType];
  const choicesRegion = settingsOptions.getIn(regionPath);
  const choicesIntrumentType = settingsOptions.getIn(instrumentTypePath);

  if (choicesRegion) {
    const find = choicesRegion.find((choice: any) => choice.get('value') === value);

    return find ? find.get('label') : value;
  } else if (choicesIntrumentType) {
    const find = choicesIntrumentType.find((choice: any) => choice.get('value') === value);

    return find ? find.get('label') : value;
  } else {
    const setting = settingsOptions.getIn([id, 'choices']).find((choice: any) => choice.get('value') === value);

    return setting ? setting.get('label') : setting;
  }
};

class AlphaCodeIntl extends React.Component<AlphaCodeGenericProps> {
  componentDidUpdate(prevProps: Readonly<AlphaCodeGenericProps>) {
    // Fetched language tokens successfully -> trigger to apply fields highlighting
    if (!this.props.isLanguageTokensFetching && prevProps.isLanguageTokensFetching) {
      this.props.editorWillMount();
    }
  }

  getMaxVisibleLines = () => (!this.props.maxHeight ? MAX_VISIBLE_LINES_DEFAULT : undefined);

  editorDidMount = (editor: any) => handleEditorDidMount(editor, this.props.codeElem, this.getMaxVisibleLines());

  editorWillMount = (monaco: any) => {
    monaco.editor.defineTheme('vs-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { background: '#2e3441' },
        { foreground: '#f7f9fb' },
        { token: 'invalid', foreground: '#df5367' },
        { token: 'string.invalid', foreground: '#df5367' },
        { token: 'nesteddots', foreground: '#df5367' },
        { token: 'number', foreground: '#7585fe' },
        { token: 'comment', foreground: '#47b74a' },
        { token: 'keyword', foreground: '#73d9ff' },
        { token: 'type', foreground: '#73d9ff' },
        { token: 'operator', foreground: '#f89602' },
        { token: 'string', foreground: '#c4df46' },
      ],
      colors: {
        'editor.background': '#2e3441',
        'editor.lineHighlightBorder': '#2e3441',
      },
    });
    this.props.editorWillMount();
  };

  render() {
    const {
      alpha,
      codeElem,
      handleCloneAlphaFromContent,
      showClone = false,
      hasTitle = true,
      modifiers = [],
      id = 'alpha-code-container',
      isPopupContent = false,
      settingsOptions,
      isMultiTab = false,
      intl,
      title = 'alphasCode.code',
      disabled,
      message,
    } = this.props;

    const author = alpha ? alpha.get('author') : '';
    const type = alpha?.get('type') ?? '';

    try {
      const settings = alpha && alpha.get('settings');
      const settingsTable = getSettingsTable(intl, type);
      let settingsTableHeader: string[] = [];
      let settingsTableValues: (number | string)[] = [];
      let stagePerformanceColumnsTypes: any = [];
      settingsTable.map((setting: any) => {
        settingsTableHeader = settingsTableHeader.concat(setting.title);
        if (settingsOptions && settingsOptions.getIn([setting.id, 'choices'])) {
          stagePerformanceColumnsTypes = stagePerformanceColumnsTypes.concat(settingsOptions.getIn([setting.id, 'type']));
          settingsTableValues = settingsTableValues.concat(
            getSettingLabel(settingsOptions, setting.id, settings.get(setting.id), settings.get('instrumentType'), settings.get('region'))
          );
        } else {
          stagePerformanceColumnsTypes = stagePerformanceColumnsTypes.concat(typeof settings.get(setting.id));
          settingsTableValues = settingsTableValues.concat(settings.get(setting.id));
        }
      });
      if (
        settingsOptions &&
        settingsOptions.getIn(['instrumentType', 'choices']) &&
        settingsOptions.getIn(['instrumentType', 'choices']).size > 1
      ) {
        settingsTableHeader.unshift(
          intl.formatMessage({ id: 'alpha.code.instrumentType', defaultMessage: localeData.en['alpha.code.instrumentType'] })
        );
        const instrumentType = settings.get('instrumentType');
        stagePerformanceColumnsTypes.unshift('choice');
        settingsTableValues.unshift(
          getSettingLabel(settingsOptions, 'instrumentType', settings.get('instrumentType'), instrumentType, instrumentType)
        );
      }

      return (
        <ErrorBoundary showUI={true} isNotFullScreen={true} isModal={true}>
          <div id={id} className={addModifiers('alpha_code', modifiers)}>
            {hasTitle && (
              <header className="alpha_code__header">
                <FormattedMessageHelper tagName="span" id={title} defaultMessage={localeData.en[title]} />
              </header>
            )}
            <div className={codeElem}>
              {alpha && !disabled ? (
                <AlphaMonaco
                  language={alpha.getIn(['settings', 'language']).toLowerCase()}
                  regular={alpha.getIn(['regular', 'code']) || alpha.get('regular')}
                  combo={alpha.getIn(['combo', 'code'])}
                  selection={alpha.getIn(['selection', 'code'])}
                  editorDidMount={this.editorDidMount}
                  editorWillMount={this.editorWillMount}
                  alphaId={alpha.get('id')}
                  type={alpha.get('type')}
                  maxVisibleLines={this.getMaxVisibleLines()}
                />
              ) : (
                <div className="alpha-code__message-not-own">
                  {message && intl.formatMessage({ id: message.id, defaultMessage: localeData.en[message.id] }, message.values)}
                </div>
              )}
            </div>

            {showClone && (
              <div className="alpha_code__actions">
                <CloneAlpha
                  alpha={alpha}
                  isMultiTab={isMultiTab}
                  userIdFromAlpha={author}
                  showHeader={false}
                  learnBlockId={this.props.learnBlockId}
                />
              </div>
            )}

            <div className={isPopupContent ? 'alpha_code__table alpha_code__table--small' : 'alpha_code__table'}>
              <h6 className="alpha_code__table-title">
                <FormattedMessageHelper
                  tagName="span"
                  id="alphasCode.simulationSettings"
                  defaultMessage={localeData.en['alphasCode.simulationSettings']}
                />
              </h6>
              <div className="alpha_code__table--data">
                {settingsTableValues && (
                  <>
                    <AlphaDataTable
                      tableRowsProperties={settingsTableHeader}
                      tableRowsRecords={[[settingsTableValues]]}
                      stagePerformanceColumnsTypes={stagePerformanceColumnsTypes}
                      dataType={stagePerformanceColumnsTypes}
                    />
                    <div className="summary-table--narrow">
                      {settingsTableValues.map((value, index) => (
                        <div key={settingsTableHeader[index]} className="alpha_code__table--data-row">
                          <span className="alpha_code__table--data-row-title">{settingsTableHeader[index]}: </span>
                          <span className="alpha_code__table--data-row-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {handleCloneAlphaFromContent !== undefined && (
              <div className="alpha_code__actions">
                <Button className="button--primary button--md" onClick={handleCloneAlphaFromContent} disabled={disabled}>
                  <FormattedMessageHelper id="alphasCode.cloneAlpha" defaultMessage={localeData.en['alphasCode.cloneAlpha']} />
                </Button>
              </div>
            )}
          </div>
        </ErrorBoundary>
      );
    } catch (error) {
      if (isPopupContent) {
        return <ErrorCatch error={error} />;
      } else {
        throw error;
      }
    }
  }
}

const mapStateToProps = (state: StateType): AlphaCodeStateToProps => ({
  isLanguageTokensFetching:
    fastExpressionFetchingSelector(state) || languageFieldsFetchingSelector(state) || expressionFetchingSelector(state),
});

export const AlphaCode = compose<any>(injectIntl, connect<AlphaCodeStateToProps, {}, any, StateType>(mapStateToProps))(AlphaCodeIntl);
